import React from 'react';
import { useSelector, useDispatch } from "react-redux";
import Select from 'react-select';
import {
  Button,
  OverlayTrigger,
  Popover,
  InputGroup,
  FormControl
} from "react-bootstrap";
import _ from "lodash";
import { shareItem } from "../actions/items";
import { copyToClipboard, MESSAGES, addEventNameToEndpoint } from "../utils/common";
import { ShareType } from "../constants/Types";
import './ButtonShareWithRecipient.scss';

/**
 * Button to share with recipients and track shares
 * props: 
 *  shortInfo
 *  itemId
 *  itemShortUrl
 *  variant
 */
const ButtonShareWithRecipient = props => {
  const [recipient, setRecipient] = React.useState("");
  const [shortInfo, setShortInfo] = React.useState(null);
  const [ctaValue, setCtaValue] = React.useState(null);
  const shareButtonRef = React.createRef();
  const dispatch = useDispatch();

  const Analytics = useSelector(({ auth }) => auth.Analytics);
  const orgShortInfo = useSelector(({ item }) => item.shortInfo);
  const ctaList = useSelector(({ settings }) => (settings.ctas || []).filter(ct => !ct.deletedAt) );

  React.useEffect(() => {
    const thisItemId = _.get(props, "itemId", null);

    if (orgShortInfo && orgShortInfo.id.includes(thisItemId)) {
      setShortInfo(orgShortInfo);
    }
  }, [orgShortInfo]);

  React.useEffect(() => {
    // console.log({function: "compDidUpdate", props: props});
    // notify on new recipient share
    const didAlert = _.get(shortInfo, "didAlert", true);
    if (!didAlert) {
      copyAndNotifyRecipientShare();

      if (props.onChangeEvent) {
        props.onChangeEvent();
      }
    }
  }, [shortInfo]);

  /**
   * Handle the short URL share with a recipient noted
   * along with the short URL
   * @param {Object} e 
   * @param {String} id itemId 
   */
  const onClickShareWithRecipient = async (e, id) => {
    // console.log({functionName: "onClickShareWithRecipient", e});
    e.stopPropagation();
    // dismiss overlay
    document.body.click();
    const sharedTo = recipient.trim();
    
    // just copy link if no recipient
    if (!sharedTo) {
      // onClickShare(e);
      return;
    }

    // we have a recipient
    const shortUrl = _.get(props, 'itemShortUrl', '');

    // clear alert status
    if (shortInfo) {
      shortInfo.didAlert = false;
    }

    const shareInfo = {
      id,
      sharedTo,
      shortUrl,
      itemType: props.itemType,
      ctaId: ctaValue ? ctaValue.value : ''
    }
    dispatch(shareItem(shareInfo));

    if (props.changeNotificationPermission) {
      props.changeNotificationPermission();
    }
  }

  const copyAndNotifyRecipientShare = () => {
    if (shortInfo && shortInfo.shareId) {
      const { shortUrl, shareId, id, tenantId, itemType, itemId } = shortInfo;
      
      const trackingUrl = `${shortUrl}?sid=${shareId}`;
      const attributes = { tenantId, shortUrl: shortUrl.replace("https://", "") };
      if (itemType === ShareType.Item || !itemType) {
        attributes.itemId = itemId;
      }
      if (itemType === ShareType.Site) {
        attributes.siteId = itemId;
      }
      
      copyToClipboard(trackingUrl, MESSAGES.URL_RECIPIENT_COPY);
      shortInfo.didAlert = true;

      // analytics
      addEventNameToEndpoint(Analytics, 'user-shorturl-share');
      Analytics.record({name: 'user-shorturl-share', attributes});
    }
  }

  const onEnterOverlay = () => {
    if (shareButtonRef && shareButtonRef.current) {
      setTimeout(() => {
        shareButtonRef.current.focus();
      }, 100);
    }
  }

  const { itemId, itemShortUrl } = props;

  return (
    <OverlayTrigger
      trigger="click"
      rootClose={true}
      key={`${itemId}-share-recipient`}
      placement="bottom"
      container={props.container}
      className="share-recipient-button"
      onClick={e => e.preventDefault()}
      onEnter={onEnterOverlay}
      overlay={
        <Popover id={`popover-positioned-bottom`}>
          <Popover.Title as="h3">Recipient</Popover.Title>
          <Popover.Content>
            <InputGroup size="sm">
              <FormControl
                ref={shareButtonRef}
                aria-label="Recipient"
                aria-describedby="Recipient"
                placeholder="Recipient..."
                value={recipient}
                className="share-recipient"
                onChange={(e) => setRecipient(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    onClickShareWithRecipient(event, itemId);
                  }
                }}
              />
              <InputGroup.Append>
                <Button
                  tabIndex="0"
                  variant="outline-secondary"
                  onClick={(e) => {
                    onClickShareWithRecipient(e, itemId);
                  }}
                >
                  Copy
                </Button>
              </InputGroup.Append>
            </InputGroup>
            {(process.env.REACT_APP_FLAG_CTA === "true") && (props.itemType === ShareType.Site) && (
              <div className="mt-2">
                <Select
                  options={ctaList.map(ct => ({ value: ct.id, label: ct.message }))}
                  classNamePrefix="cta_"
                  placeholder="Select CTA"
                  onChange={value => setCtaValue(value)}
                  size={14}
                  value={ctaValue}
                />
              </div>
            )}
          </Popover.Content>
        </Popover>
      }
    >
      <Button
        variant={_.get(props, "variant", "outline-danger")}
        className="btn btn-outline-danger"
        size="sm"
        data-cc-analytics-on="click"
        id="btnItemUrlShare"
        // we capture the share event as a copy here
        // but as a share in the method
        data-cc-analytics-name="user-shorturl-copy"
        data-cc-analytics-attrs={`${props.itemType === ShareType.Site ? "siteId" : "itemId"}:${itemId},shortUrl:${(itemShortUrl || '').replace("https://", "")}`}
        onClick={e => {
          e.preventDefault();
          return true;
        }}
      >
        SHARE
      </Button>
    </OverlayTrigger>
  );
}

export default ButtonShareWithRecipient;
